import * as React from 'react';

import cx from 'classnames';

import { ReactNodeNoStrings } from '~shared/types/ReactNodeNoStrings';

import Tooltip from '../Tooltip/Tooltip';
import { RadioGroupContext, RadioItemContext } from './RadioGroupContext';

type FormInputProps = React.AllHTMLAttributes<HTMLInputElement>;

interface RadioItemRenderProps {
  checked: boolean;
  disabled: boolean;
}

export interface RadioItemProps {
  children?: (props: RadioItemRenderProps) => ReactNodeNoStrings;
  id?: FormInputProps['id'];
  value?: FormInputProps['value'];
  onChange?: NonNullable<FormInputProps['onChange']>;
  label?: string;
  /** Use a Tooltip component to display the associated label. */
  useTooltipLabel?: boolean;
}

export const RadioItem = React.forwardRef<HTMLInputElement, RadioItemProps>(
  function RadioItem(
    { children, value, label, useTooltipLabel, ...props }: RadioItemProps,
    ref
  ) {
    const radioGroupContext = React.useContext(RadioGroupContext);
    const radioItemContext = React.useContext(RadioItemContext);

    const checked = radioGroupContext.value === value;
    const isFirstRadioWithNoCheckedValueInGroup =
      radioItemContext === 0 && !radioGroupContext.value;
    const tababble = checked || isFirstRadioWithNoCheckedValueInGroup;

    const inputClass = cx(
      'absolute inset-0 w-full h-full z-10 m-0 opacity-0 border-0',
      {
        'cursor-pointer': !radioGroupContext.disabled,
      }
    );

    return (
      <label className="group relative flex rounded-pill radio-item">
        {useTooltipLabel ? (
          <Tooltip label={label}>
            <input
              {...props}
              className={inputClass}
              id={`${radioGroupContext.id}_${radioItemContext}`}
              type="radio"
              name={radioGroupContext.name}
              value={value}
              onChange={radioGroupContext.onChange}
              checked={checked}
              disabled={radioGroupContext.disabled}
              tabIndex={tababble ? 0 : -1}
              ref={ref}
            />
          </Tooltip>
        ) : (
          <input
            {...props}
            className={inputClass}
            id={`${radioGroupContext.id}_${radioItemContext}`}
            type="radio"
            name={radioGroupContext.name}
            value={value}
            onChange={radioGroupContext.onChange}
            checked={checked}
            disabled={radioGroupContext.disabled}
            tabIndex={tababble ? 0 : -1}
            aria-label={label}
            ref={ref}
          />
        )}
        {/** @todo Define some default indicator component. */}
        {children
          ? children({ checked, disabled: radioGroupContext.disabled })
          : null}
      </label>
    );
  }
);
